import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase-config';

/** Session key: after login from home, App sends the owner back here. */
export const PENDING_OWNER_RATE_JOB_SESSION_KEY = 'pendingOwnerRateJobRequestId';

function formatCategory(cat) {
  if (!cat || typeof cat !== 'string') return '';
  return cat.replace(/_/g, ' ');
}

const OwnerRateProfessionalPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setAuthUser);
    return () => unsub();
  }, []);

  const loadRequest = useCallback(async () => {
    if (!requestId) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'maintenance_requests', requestId));
      if (!snap.exists()) {
        setRequest(null);
        setError('This maintenance request was not found.');
        setLoading(false);
        return;
      }
      setRequest({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Could not load this request.');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (!authUser) {
      setLoading(false);
      setRequest(null);
      setError('');
      return;
    }
    loadRequest();
  }, [authUser, loadRequest]);

  const submitReview = async () => {
    if (!request?.id || !authUser?.uid) return;
    if (!stars || stars < 1 || stars > 5) {
      window.alert('Please choose a rating from 1 to 5 stars.');
      return;
    }
    const commentRaw = (comment || '').trim();
    const ownerRating = {
      stars,
      ownerId: authUser.uid,
      createdAt: new Date(),
    };
    if (commentRaw) {
      ownerRating.comment = commentRaw.slice(0, 2000);
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, 'maintenance_requests', request.id), { ownerRating });
      await loadRequest();
      setStars(0);
      setComment('');
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Could not save your review.');
    } finally {
      setSaving(false);
    }
  };

  const storePendingAndGoHome = () => {
    if (requestId) {
      try {
        sessionStorage.setItem(PENDING_OWNER_RATE_JOB_SESSION_KEY, requestId);
      } catch (e) {
        console.warn(e);
      }
    }
    navigate('/#auth');
  };

  const handleInlineLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginBusy(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
    } catch (err) {
      setLoginError(err?.message || 'Sign-in failed.');
    } finally {
      setLoginBusy(false);
    }
  };

  const publicBase = process.env.PUBLIC_URL || '';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
        padding: '32px 16px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 520,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '24px 28px',
            textAlign: 'center',
            background: 'linear-gradient(180deg, #f1f5f9 0%, #ffffff 100%)',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <img
            src={`${publicBase}/tenant-dispatch-icon-192.png`}
            alt=""
            style={{ maxWidth: 160, height: 'auto', marginBottom: 12 }}
          />
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
            Rate your service professional
          </h1>
          <p style={{ margin: '10px 0 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
            Optional feedback after a completed maintenance visit. Your review helps other owners and tenants.
          </p>
        </div>

        <div style={{ padding: 28 }}>
          {!authUser ? (
            <>
              <p style={{ margin: '0 0 16px', fontSize: 14, color: '#334155', lineHeight: 1.55 }}>
                Sign in with the <strong>property owner</strong> account that manages this unit, then you can submit a star rating and short comment.
              </p>
              <form onSubmit={handleInlineLogin} style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                  Email
                </label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(ev) => setLoginEmail(ev.target.value)}
                  autoComplete="email"
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    marginBottom: 12,
                    fontSize: 15,
                  }}
                />
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                  Password
                </label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(ev) => setLoginPassword(ev.target.value)}
                  autoComplete="current-password"
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    marginBottom: 12,
                    fontSize: 15,
                  }}
                />
                {loginError ? (
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b91c1c' }}>{loginError}</p>
                ) : null}
                <button
                  type="submit"
                  disabled={loginBusy}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: 'none',
                    background: loginBusy ? '#94a3b8' : '#1e293b',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: loginBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loginBusy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={storePendingAndGoHome}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#4f46e5',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: 13,
                    padding: 0,
                  }}
                >
                  Open home &amp; sign in there
                </button>
                {' · '}
                <Link to="/" style={{ color: '#4f46e5', fontWeight: 600 }}>
                  Back to home
                </Link>
              </p>
            </>
          ) : loading ? (
            <p style={{ margin: 0, textAlign: 'center', color: '#64748b' }}>Loading…</p>
          ) : error ? (
            <p style={{ margin: 0, color: '#b91c1c', fontSize: 14 }}>{error}</p>
          ) : !request ? (
            <p style={{ margin: 0, color: '#64748b' }}>Nothing to show.</p>
          ) : request.ownerId !== authUser.uid ? (
            <p style={{ margin: 0, fontSize: 14, color: '#b91c1c', lineHeight: 1.5 }}>
              You are signed in, but this job belongs to a different owner account. Please switch to the owner account for this property.
            </p>
          ) : request.status !== 'completed' ? (
            <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.55 }}>
              This job is not marked complete yet. When the professional marks it complete, you can leave a review from this link or from your owner dashboard.
            </p>
          ) : !request.assignedProfessional ? (
            <p style={{ margin: 0, fontSize: 14, color: '#334155' }}>
              No professional was assigned on this request, so a review is not available.
            </p>
          ) : request.ownerRating?.stars ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#065f46' }}>Thank you</p>
              <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>Your review has been saved.</p>
              <p style={{ margin: '16px 0 0', fontSize: 22, letterSpacing: 2, color: '#ca8a04' }}>
                {'★'.repeat(request.ownerRating.stars)}
                {'☆'.repeat(5 - request.ownerRating.stars)}
              </p>
              <Link
                to="/owner"
                style={{
                  display: 'inline-block',
                  marginTop: 24,
                  color: '#4f46e5',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                Go to owner dashboard
              </Link>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, padding: 14, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Job
                </p>
                {request.unitNumber ? (
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Unit {request.unitNumber}</p>
                ) : null}
                {request.category ? (
                  <p style={{ margin: '6px 0 0', fontSize: 14, color: '#475569' }}>{formatCategory(request.category)}</p>
                ) : null}
                {request.description ? (
                  <p style={{ margin: '10px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
                    {String(request.description).slice(0, 280)}
                    {String(request.description).length > 280 ? '…' : ''}
                  </p>
                ) : null}
              </div>
              <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Your rating</p>
              <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setStars(n)}
                    disabled={saving}
                    style={{
                      fontSize: 28,
                      lineHeight: 1,
                      border: 'none',
                      background: 'transparent',
                      cursor: saving ? 'default' : 'pointer',
                      color: stars >= n ? '#ca8a04' : '#d1d5db',
                      padding: '2px 4px',
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                Comments (optional)
              </label>
              <textarea
                value={comment}
                onChange={(ev) => setComment(ev.target.value.slice(0, 2000))}
                disabled={saving}
                rows={4}
                placeholder="What went well? Anything others should know?"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 14,
                  marginBottom: 14,
                }}
              />
              <button
                type="button"
                onClick={submitReview}
                disabled={saving || !stars}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: 'none',
                  background: saving || !stars ? '#cbd5e1' : '#1e293b',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: saving || !stars ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Submitting…' : 'Submit review'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerRateProfessionalPage;
